import { useMemo } from 'react';
import { ImageOff, ExternalLink, Smartphone, Truck, MessageSquare, Send } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  renderPreview,
  type KakaoButtonType,
  type TemplateButton,
  type TemplateVariable,
} from '../lib/kakaoTemplate';
import type { KakaoTemplateType } from '../types/notificationTemplate';

/**
 * 카카오 알림톡 말풍선 미리보기(기본형/강조표기형/이미지형) — 디자인 스펙 §7.5.
 *
 * 에버라인 중립 채팅 캔버스(카카오 옐로 미사용). #{변수} 는 예시값으로 치환(없으면 토큰 유지,
 * accent mono 하이라이트). 전부 토큰·프리미티브로만 구성, raw hex 없음.
 */
export interface KakaoBubblePreviewProps {
  type: KakaoTemplateType;
  channelName?: string | null;
  body: string;
  emphasisTitle?: string | null;
  emphasisSubtitle?: string | null;
  imageUrl?: string | null;
  buttons?: ReadonlyArray<TemplateButton>;
  variables?: ReadonlyArray<TemplateVariable>;
}

const BUTTON_ICON: Record<KakaoButtonType, typeof ExternalLink> = {
  WL: ExternalLink,
  AL: Smartphone,
  DS: Truck,
  BK: MessageSquare,
  MD: Send,
};

/** 본문을 예시값으로 치환하되 미치환 #{토큰} 은 accent mono 조각으로 분리 렌더. */
const renderBodySegments = (body: string, variables: ReadonlyArray<TemplateVariable>) => {
  const rendered = renderPreview(body, variables);
  const parts = rendered.split(/(#\{[A-Za-z0-9_]+\})/g);
  return parts.map((part, i) => {
    if (/^#\{[A-Za-z0-9_]+\}$/.test(part)) {
      return (
        <span
          key={i}
          className="font-mono"
          style={{ color: 'var(--accent-default)' }}
        >
          {part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

export const KakaoBubblePreview = ({
  type,
  channelName,
  body,
  emphasisTitle,
  emphasisSubtitle,
  imageUrl,
  buttons = [],
  variables = [],
}: KakaoBubblePreviewProps) => {
  const { t } = useTranslation('notifications');
  const bodySegments = useMemo(() => renderBodySegments(body, variables), [body, variables]);
  const hasBody = body.trim().length > 0;

  return (
    <div
      className="rounded-[var(--radius-lg)] p-4"
      style={{ background: 'var(--surface-1)' }}
      data-testid="kakao-bubble-preview"
    >
      {/* 채널 헤더 */}
      <div className="mb-2 flex items-center gap-2">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold"
          style={{ background: 'var(--surface-0)', color: 'var(--content-muted)' }}
        >
          {(channelName ?? '').trim().charAt(0) || '#'}
        </span>
        <span className="text-[12px] font-medium" style={{ color: 'var(--content-default)' }}>
          {channelName?.trim() || t('template.preview.kakaoBadge', '알림톡')}
        </span>
      </div>

      {/* 말풍선 */}
      <div
        className="max-w-[320px] overflow-hidden rounded-[var(--radius-lg)] rounded-tl-[4px]"
        style={{ background: 'var(--surface-0)', boxShadow: 'var(--elevation-1)' }}
      >
        {/* 이미지형 — 상단 이미지 */}
        {type === 'image' ? (
          imageUrl?.trim() ? (
            // eslint-disable-next-line jsx-a11y/img-redundant-alt
            <img
              src={imageUrl}
              alt={t('template.field.imageUrl', '이미지 URL')}
              className="aspect-[2/1] w-full object-cover"
            />
          ) : (
            <div
              className="flex aspect-[2/1] w-full flex-col items-center justify-center gap-1"
              style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}
            >
              <ImageOff size={20} aria-hidden="true" />
              <span className="text-[11px]">{t('template.field.imageUrl', '이미지 URL')}</span>
            </div>
          )
        ) : null}

        {/* 강조표기형 — 강조 헤더 */}
        {type === 'emphasis' ? (
          <div className="px-4 pb-1 pt-4">
            <p
              className="line-clamp-2 text-[18px] font-bold"
              style={{ color: 'var(--content-strong)', lineHeight: 'var(--leading-heading)' }}
            >
              {emphasisTitle?.trim() || t('template.field.emphasisTitle', '강조 제목')}
            </p>
            {emphasisSubtitle?.trim() ? (
              <p className="mt-0.5 truncate text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {emphasisSubtitle}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* 본문 */}
        <div
          className={type === 'emphasis' ? 'px-4 pb-4 pt-1' : 'p-4'}
        >
          {hasBody ? (
            <p
              className="whitespace-pre-wrap text-[13px]"
              style={{ color: 'var(--content-strong)', lineHeight: 'var(--leading-body)' }}
            >
              {bodySegments}
            </p>
          ) : (
            <p className="text-[13px]" style={{ color: 'var(--content-soft)' }}>
              {t('template.preview.emptyBody', '본문을 입력하면 미리보기가 표시됩니다.')}
            </p>
          )}
        </div>

        {/* 버튼 영역 */}
        {buttons.length > 0 ? (
          <div className="flex flex-col">
            {buttons.map((b, i) => {
              const Icon = BUTTON_ICON[b.type];
              return (
                <div
                  key={`${b.name}-${i}`}
                  className="flex h-9 items-center justify-center gap-1.5 text-[13px]"
                  style={{
                    borderTop: '1px solid var(--divider-soft)',
                    color: 'var(--content-default)',
                  }}
                >
                  <Icon size={13} aria-hidden="true" style={{ color: 'var(--content-muted)' }} />
                  <span>{b.name.trim() || t('template.buttons.name', '버튼명')}</span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export interface SmsBubblePreviewProps {
  body: string;
  variables?: ReadonlyArray<TemplateVariable>;
  /** 하단 캡션(예: "012 byte · SMS"). */
  caption?: string;
}

/** SMS/문자 단순 말풍선 미리보기(헤더·버튼 없음) — 디자인 스펙 §7.5 보조. */
export const SmsBubblePreview = ({ body, variables = [], caption }: SmsBubblePreviewProps) => {
  const { t } = useTranslation('notifications');
  const bodySegments = useMemo(() => renderBodySegments(body, variables), [body, variables]);
  const hasBody = body.trim().length > 0;
  return (
    <div
      className="rounded-[var(--radius-lg)] p-4"
      style={{ background: 'var(--surface-1)' }}
      data-testid="sms-bubble-preview"
    >
      <div
        className="max-w-[320px] overflow-hidden rounded-[var(--radius-lg)] rounded-tl-[4px] p-4"
        style={{ background: 'var(--surface-0)', boxShadow: 'var(--elevation-1)' }}
      >
        {hasBody ? (
          <p
            className="whitespace-pre-wrap text-[13px]"
            style={{ color: 'var(--content-strong)', lineHeight: 'var(--leading-body)' }}
          >
            {bodySegments}
          </p>
        ) : (
          <p className="text-[13px]" style={{ color: 'var(--content-soft)' }}>
            {t('template.preview.emptyBody', '본문을 입력하면 미리보기가 표시됩니다.')}
          </p>
        )}
      </div>
      {caption ? (
        <p className="mt-2 text-[11px]" style={{ color: 'var(--content-muted)' }}>
          {caption}
        </p>
      ) : null}
    </div>
  );
};
